import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Check } from "lucide-react";
import { ksUserQK } from "../../lib/query-keys";
import { useUpdateEmail } from "../../lib/hooks/useUpdateEmail";

const schema = z.object({
  email: z.string().email("Adresse e-mail invalide"),
  password: z.string().min(1, "Requis"),
});
type FieldValues = z.infer<typeof schema>;

const UpdateEmailDialog = () => {
  const queryclient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { mutate: updateEmail, isPending } = useUpdateEmail();
  const form = useForm<FieldValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: "",
      email: "",
    },
  });

  const update = (data: FieldValues) => {
    updateEmail(
      {
        newEmail: data.email.trim().toLowerCase(),
        password: data.password,
      },
      {
        onSuccess: () => {
          setOpen(false);
          toast.success("Votre adresse e-mail a été modifiée");
          queryclient.invalidateQueries({ queryKey: [ksUserQK] });
        },
        onError: (err: any) => {
          console.error(err.response?.data || err);
          toast.error(err.response?.data?.message || `Une erreur s'est produite`);
        },
      }
    );
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpened) => {
        form.reset();
        setOpen(isOpened);
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto">Modifier l'adresse e-mail</Button>
      </DialogTrigger>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[440px] sm:rounded-2xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit((data) => update(data))}>
            <DialogHeader className="border-b border-slate-100 bg-[#fbfaf6] px-6 py-5 pr-12">
              <DialogTitle className="text-xl text-slate-950">Modification d'adresse e-mail</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-5 px-6 py-5 [&_input]:h-11 [&_input]:border-slate-300">
              {/* Body */}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mot de passe</FormLabel>
                    <FormControl>
                      <Input type="password" required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nouvelle adresse e-mail</FormLabel>
                    <FormControl>
                      <Input type="email" required {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="border-t border-slate-100 bg-slate-50/70 px-6 py-4">
              <DialogClose asChild>
                <Button variant="outline">Fermer</Button>
              </DialogClose>
              <Button
                icon={<Check className="h-4 w-4" />}
                loading={isPending}
                className="bg-[#173f35] hover:bg-[#23594a]"
              >
                Valider
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateEmailDialog;
